import { get, onValue, ref, remove, runTransaction, set, update, type Unsubscribe } from 'firebase/database';
import type { User } from 'firebase/auth';
import { database } from './firebase';
import { mergeScores } from './merge';
import type { ScoreConflict, ScoreDoc } from '../types';

interface CloudEnvelope { revision: number; updatedAt: number; updatedBy: string; state: ScoreDoc }
const INLINE_ASSET_LIMIT = 1_200_000;

function inviteKey(email: string) {
  // RTDB keys cannot contain periods; encodeURIComponent intentionally leaves them alone.
  return encodeURIComponent(email.trim().toLowerCase()).replace(/\./g, '%2E');
}

function removeUndefined<T>(value: T): T {
  if (Array.isArray(value)) return value.map((item) => item === undefined ? null : removeUndefined(item)) as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => item !== undefined)
      .map(([key, item]) => [key, removeUndefined(item)])) as T;
  }
  return value;
}

export async function createCloudScore(score: ScoreDoc, user: User) {
  if (!database) throw new Error('Firebase 尚未設定');
  const now = Date.now();
  const initial = structuredClone(score);
  if (initial.sourceDataUrl && initial.sourceDataUrl.length > INLINE_ASSET_LIMIT) initial.sourceDataUrl = undefined;
  initial.objects = initial.objects.map((object) => object.kind === 'image' && object.assetUrl && object.assetUrl.length > INLINE_ASSET_LIMIT
    ? { ...object, assetUrl: undefined, assetKey: undefined }
    : object);
  const state = removeUndefined({ ...initial, ownerUid: user.uid, updatedAt: now });
  await set(ref(database, `scoreMembers/${score.id}/${user.uid}`), { role: 'owner', email: user.email ?? '' });
  await set(ref(database, `userScores/${user.uid}/${score.id}`), 'owner');
  await set(ref(database, `scoreState/${score.id}`), { revision: 1, updatedAt: now, updatedBy: user.uid, state } satisfies CloudEnvelope);
  return state;
}

export async function loadCloudScore(scoreId: string) {
  if (!database) return undefined;
  const snapshot = await get(ref(database, `scoreState/${scoreId}`));
  const value = snapshot.val() as CloudEnvelope | null;
  return value?.state;
}

export async function deleteOwnedCloudScore(scoreId: string, user: User) {
  if (!database) throw new Error('Firebase 尚未設定');
  const current = await loadCloudScore(scoreId);
  if (!current || current.ownerUid !== user.uid) return false;
  await update(ref(database), {
    [`scoreState/${scoreId}`]: null,
    [`scoreMembers/${scoreId}/${user.uid}`]: null,
    [`userScores/${user.uid}/${scoreId}`]: null,
  });
  return true;
}

async function uploadAsset(scoreId: string, name: string, mimeType: string, dataUrl: string, user: User) {
  const token = await user.getIdToken();
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const response = await fetch('/api/assets', {
    method: 'POST', headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ scoreId, name, mimeType, dataBase64: base64 }),
  });
  if (!response.ok) return undefined;
  const result = await response.json() as { key?: string };
  return result.key;
}

export async function prepareCloudScore(score: ScoreDoc, user: User): Promise<{ local: ScoreDoc; cloud: ScoreDoc }> {
  const local = structuredClone(score);
  const cloud = structuredClone(score);
  const inlineLimit = INLINE_ASSET_LIMIT;
  if (cloud.sourceDataUrl && cloud.sourceDataUrl.length > inlineLimit) {
    const key = cloud.sourceAssetKey ?? await uploadAsset(score.id, cloud.sourceName ?? 'score-source', cloud.sourceMimeType ?? 'application/octet-stream', cloud.sourceDataUrl, user);
    if (!key) throw new Error('這個譜面檔案較大，需先在 Cloudflare 綁定 R2 儲存空間；離線檔案仍保存在本機。');
    local.sourceAssetKey = key; cloud.sourceAssetKey = key; cloud.sourceDataUrl = undefined;
  }
  for (let index = 0; index < cloud.objects.length; index += 1) {
    const object = cloud.objects[index];
    if (object.kind !== 'image' || !object.assetUrl || object.assetUrl.length <= inlineLimit) continue;
    const imageMimeType = object.assetUrl.match(/^data:([^;,]+)/)?.[1] ?? 'image/png';
    const key = object.assetKey ?? await uploadAsset(score.id, `annotation-${object.id}`, imageMimeType, object.assetUrl, user);
    if (!key) throw new Error('標註圖片較大，需在 Cloudflare 綁定 R2；圖片仍保存在本機。');
    local.objects[index] = { ...local.objects[index], assetKey: key };
    cloud.objects[index] = { ...object, assetKey: key, assetUrl: undefined };
  }
  return { local, cloud };
}

async function fetchAsset(scoreId: string, key: string, user: User) {
  const token = await user.getIdToken();
  const response = await fetch(`/api/assets?scoreId=${encodeURIComponent(scoreId)}&key=${encodeURIComponent(key)}`, { headers: { authorization: `Bearer ${token}` } });
  if (!response.ok) return undefined;
  const blob = await response.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader(); reader.onload = () => resolve(String(reader.result)); reader.onerror = () => reject(reader.error); reader.readAsDataURL(blob);
  });
}

export async function hydrateCloudScore(score: ScoreDoc, user: User): Promise<ScoreDoc> {
  const local = structuredClone(score);
  if (local.sourceAssetKey && !local.sourceDataUrl) local.sourceDataUrl = await fetchAsset(local.id, local.sourceAssetKey, user);
  for (let index = 0; index < local.objects.length; index += 1) {
    const object = local.objects[index];
    if (object.assetKey && !object.assetUrl) local.objects[index] = { ...object, assetUrl: await fetchAsset(local.id, object.assetKey, user) };
  }
  return local;
}

export function subscribeCloudScore(scoreId: string, listener: (score: ScoreDoc | undefined, envelope?: CloudEnvelope) => void): Unsubscribe | undefined {
  if (!database) return undefined;
  return onValue(ref(database, `scoreState/${scoreId}`), (snapshot) => {
    const value = snapshot.val() as CloudEnvelope | null;
    listener(value?.state, value ?? undefined);
  });
}

export async function pushCloudScore(local: ScoreDoc, base: ScoreDoc | undefined, user: User): Promise<{ score: ScoreDoc; conflicts: ScoreConflict[] }> {
  const db = database;
  if (!db) throw new Error('Firebase 尚未設定');
  const conflictBuffer: ScoreConflict[] = [];
  const transaction = await runTransaction(ref(db, `scoreState/${local.id}`), (currentValue) => {
    const current = currentValue as CloudEnvelope | null;
    if (!current) return removeUndefined({ revision: 1, updatedAt: Date.now(), updatedBy: user.uid, state: { ...local, ownerUid: user.uid, updatedAt: Date.now() } } satisfies CloudEnvelope);
    if (!base) return undefined;
    const merged = mergeScores(base, local, current.state);
    conflictBuffer.splice(0, conflictBuffer.length, ...merged.conflicts);
    return removeUndefined({ revision: current.revision + 1, updatedAt: Date.now(), updatedBy: user.uid, state: { ...merged.score, updatedAt: Date.now() } } satisfies CloudEnvelope);
  }, { applyLocally: false });
  if (!transaction.committed || !transaction.snapshot.exists()) throw new Error('雲端同步未完成，離線修改已保留在本機。');
  const envelope = transaction.snapshot.val() as CloudEnvelope;
  if (conflictBuffer.length) {
    await Promise.all(conflictBuffer.map((conflict) => set(ref(db, `scoreConflicts/${local.id}/${encodeURIComponent(conflict.id)}`), removeUndefined(conflict))));
  }
  return { score: envelope.state, conflicts: conflictBuffer };
}

export function subscribeScoreConflicts(scoreId: string, listener: (conflicts: ScoreConflict[]) => void): Unsubscribe | undefined {
  if (!database) return undefined;
  return onValue(ref(database, `scoreConflicts/${scoreId}`), (snapshot) => {
    listener(Object.values((snapshot.val() ?? {}) as Record<string, ScoreConflict>));
  });
}

export async function clearScoreConflict(conflict: ScoreConflict) {
  if (!database) return;
  await remove(ref(database, `scoreConflicts/${conflict.scoreId}/${encodeURIComponent(conflict.id)}`));
}

export async function listCloudScores(user: User) {
  if (!database) return [] as Array<{ scoreId: string; role: string }>;
  const snapshot = await get(ref(database, `userScores/${user.uid}`));
  const entries = Object.entries((snapshot.val() ?? {}) as Record<string, string>);
  return entries.map(([scoreId, role]) => ({ scoreId, role }));
}

export async function inviteByEmail(scoreId: string, email: string, role: 'editor' | 'viewer') {
  if (!database) throw new Error('Firebase 尚未設定');
  const clean = email.trim().toLowerCase();
  if (!clean.includes('@')) throw new Error('請輸入有效的 Google 帳號 email。');
  await set(ref(database, `scoreInvites/${scoreId}/${inviteKey(clean)}`), { email: clean, role, createdAt: Date.now() });
}

export async function requestInviteAcceptance(scoreId: string, user: User) {
  if (!database || !user.email) return undefined;
  const emailKey = inviteKey(user.email);
  const snapshot = await get(ref(database, `scoreInvites/${scoreId}/${emailKey}`));
  const invite = snapshot.val() as { email: string; role: 'editor' | 'viewer' } | null;
  if (!invite || invite.email.toLowerCase() !== user.email.toLowerCase()) return undefined;
  await set(ref(database, `scoreRequests/${scoreId}/${emailKey}`), { uid: user.uid, email: user.email, role: invite.role, createdAt: Date.now() });
  return invite.role;
}

export async function listInvites(scoreId: string) {
  if (!database) return [] as Array<{ email: string; role: 'editor' | 'viewer' }>;
  const snapshot = await get(ref(database, `scoreInvites/${scoreId}`));
  return Object.values((snapshot.val() ?? {}) as Record<string, { email: string; role: 'editor' | 'viewer' }>);
}

export async function listScoreRequests(scoreId: string) {
  if (!database) return [] as Array<{ uid: string; email: string; role: 'editor' | 'viewer'; createdAt: number }>;
  const snapshot = await get(ref(database, `scoreRequests/${scoreId}`));
  return Object.values((snapshot.val() ?? {}) as Record<string, { uid: string; email: string; role: 'editor' | 'viewer'; createdAt: number }>);
}

export async function approveScoreRequest(scoreId: string, request: { uid: string; email: string; role: 'editor' | 'viewer' }) {
  if (!database) throw new Error('Firebase 尚未設定');
  await set(ref(database, `scoreMembers/${scoreId}/${request.uid}`), { role: request.role, email: request.email });
  await set(ref(database, `userScores/${request.uid}/${scoreId}`), request.role);
  await remove(ref(database, `scoreRequests/${scoreId}/${inviteKey(request.email)}`));
  await remove(ref(database, `scoreInvites/${scoreId}/${inviteKey(request.email)}`));
}
